import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faTimes, faCheck } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import api from '../Api';

const NotificationBell = () => {
    const [unreadCount, setUnreadCount] = useState(0);
    const [notifications, setNotifications] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isNewNotification, setIsNewNotification] = useState(false);
    const dropdownRef = useRef(null);
    const previousUnreadCountRef = useRef(0);
    const isInitialLoadRef = useRef(true);
    const navigate = useNavigate();
    
    // Function to play notification sound - softer, gentler tone
    const playNotificationSound = useCallback(() => {
        try {
            // Create a soft, pleasant notification sound using Web Audio API
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            
            // Create a softer tone with lower frequency
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            
            // Add a subtle reverb/softness with a low-pass filter
            const filter = audioContext.createBiquadFilter();
            filter.type = 'lowpass';
            filter.frequency.setValueAtTime(800, audioContext.currentTime);
            
            oscillator.connect(filter);
            filter.connect(gainNode);
            gainNode.connect(audioContext.destination);
            
            // Use lower, softer frequency (bell-like sound)
            oscillator.frequency.setValueAtTime(523, audioContext.currentTime); // C5 note
            oscillator.frequency.linearRampToValueAtTime(659, audioContext.currentTime + 0.15); // E5 note
            oscillator.type = 'sine'; // Softest waveform
            
            // Much softer volume and gentler envelope
            gainNode.gain.setValueAtTime(0, audioContext.currentTime);
            gainNode.gain.linearRampToValueAtTime(0.12, audioContext.currentTime + 0.05); // Very soft start
            gainNode.gain.linearRampToValueAtTime(0.08, audioContext.currentTime + 0.15);
            gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.4); // Gentle fade
            
            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.4);
        } catch (error) {
            console.warn('Could not play notification sound:', error);
            // Fallback: try to use a softer beep if Web Audio API fails
            try {
                const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2+axdyIFNIXS8tiJURE=');
                audio.volume = 0.15; // Reduced fallback volume too
                audio.play().catch(() => {}); // Silent fail if audio cannot play
            } catch (fallbackError) {
                console.warn('Fallback audio also failed:', fallbackError);
            }
        }
    }, []);

    const fetchUnreadCount = useCallback(async () => {
        try {
            const response = await api.get('/notifications/unread-count');
            if (response.data.success) {
                const newCount = response.data.count;
                const previousCount = previousUnreadCountRef.current;
                
                // Play sound and show visual feedback if unread count increased (new notification) and not initial load
                if (!isInitialLoadRef.current && newCount > previousCount) {
                    playNotificationSound();
                    // Add visual feedback
                    setIsNewNotification(true);
                    setTimeout(() => setIsNewNotification(false), 3000); // Clear after 3 seconds
                }
                
                // Update refs for next comparison
                previousUnreadCountRef.current = newCount;
                setUnreadCount(newCount);
                isInitialLoadRef.current = false;
            }
        } catch (error) {
            // Don't log 429 errors repeatedly to avoid spam
            if (error.response?.status !== 429) {
                console.error('Error fetching unread count:', error);
            }
            // If rate limited, stop polling temporarily
            if (error.response?.status === 429) {
                // Clear interval and restart after 30 seconds
                return;
            }
        }
    }, [playNotificationSound]);
    
    useEffect(() => {
        fetchUnreadCount();

        // Poll every 30 seconds for new notifications (reduced frequency to lower server load)
        let interval = null;

        const startPolling = () => {
            if (!interval) {
                interval = setInterval(() => {
                    // Only fetch if tab is visible
                    if (!document.hidden) {
                        fetchUnreadCount().catch(() => {});
                    }
                }, 30000);
            }
        };

        const stopPolling = () => {
            if (interval) {
                clearInterval(interval);
                interval = null;
            }
        };

        // Handle visibility change - pause polling when tab is hidden
        const handleVisibilityChange = () => {
            if (document.hidden) {
                stopPolling();
            } else {
                // Fetch immediately when tab becomes visible, then resume polling
                fetchUnreadCount().catch(() => {});
                startPolling();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Start polling if tab is visible
        if (!document.hidden) {
            startPolling();
        }

        return () => {
            stopPolling();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [fetchUnreadCount]);
    
    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    
    const fetchNotifications = async () => {
        try {
            setLoading(true);
            const response = await api.get('/notifications?per_page=10');
            if (response.data.success) {
                setNotifications(response.data.data.data);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    };
    
    const handleBellClick = () => {
        if (!showDropdown) {
            fetchNotifications();
        }
        setShowDropdown(!showDropdown);
    };
    
    const handleNotificationClick = async (notification) => {
        try {
            // Mark as read
            if (!notification.read_at) {
                await api.post(`/notifications/${notification.id}/mark-read`);
            }
            
            // Navigate to the URL
            if (notification.data.url) {
                navigate(notification.data.url);
            }
            
            // Update counts and close dropdown
            fetchUnreadCount();
            setShowDropdown(false);
        } catch (error) {
            console.error('Error handling notification click:', error);
        }
    };
    
    const handleMarkAllRead = async () => {
        try {
            const response = await api.post('/notifications/mark-all-read');
            if (response.data.success) {
                fetchNotifications();
                fetchUnreadCount();
            }
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };
    
    const handleViewAllNotifications = () => {
        setShowDropdown(false);
        navigate('/notifications');
    };
    
    const formatTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);
        
        if (diffInSeconds < 60) return 'À l\'instant';
        if (diffInSeconds < 3600) return `Il y a ${Math.floor(diffInSeconds / 60)} min`;
        if (diffInSeconds < 86400) return `Il y a ${Math.floor(diffInSeconds / 3600)} h`;
        if (diffInSeconds < 604800) return `Il y a ${Math.floor(diffInSeconds / 86400)} j`;
        return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    };
    
    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={handleBellClick}
                className={`relative p-2 text-gray-600 hover:text-gray-900 transition-all duration-300 focus:outline-none ${
                    isNewNotification ? 'animate-bounce text-red-600' : ''
                }`}
                aria-label="Notifications"
            >
                <FontAwesomeIcon icon={faBell} className="text-xl" />
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold animate-pulse">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>
            
            {showDropdown && (
                <div className="fixed sm:absolute right-4 sm:right-0 top-16 sm:top-auto sm:mt-2 w-[calc(100vw-2rem)] sm:w-80 md:w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[32rem] flex flex-col">
                    {/* Header */}
                    <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200">
                        <h3 className="font-semibold text-gray-900 text-base sm:text-lg">Notifications</h3>
                        <div className="flex items-center space-x-2">
                            {notifications.some(n => !n.read_at) && (
                                <button
                                    onClick={handleMarkAllRead}
                                    className="text-xs text-red-600 hover:text-red-700 font-medium transition-colors"
                                    title="Tout marquer comme lu"
                                >
                                    <FontAwesomeIcon icon={faCheck} className="mr-1" />
                                    <span className="hidden sm:inline">Tout lire</span>
                                    <span className="sm:hidden">Tout</span>
                                </button>
                            )}
                            <button
                                onClick={() => setShowDropdown(false)}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                                aria-label="Fermer"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                    </div>
                    
                    {/* Notifications List */}
                    <div className="overflow-y-auto flex-1">
                        {loading ? (
                            <div className="p-8 text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
                                <p className="text-gray-500 mt-2">Chargement...</p>
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="p-8 text-center">
                                <FontAwesomeIcon icon={faBell} className="text-4xl text-gray-300 mb-3" />
                                <p className="text-gray-500 font-medium">Aucune notification</p>
                                <p className="text-gray-400 text-sm mt-1">Vous êtes à jour !</p>
                            </div>
                        ) : (
                            <div>
                                {notifications.map((notification) => (
                                    <div
                                        key={notification.id}
                                        onClick={() => handleNotificationClick(notification)}
                                        className={`p-3 sm:p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                                            !notification.read_at ? 'bg-blue-50' : ''
                                        }`}
                                    >
                                        <div className="flex-1">
                                            <p className={`text-xs sm:text-sm ${!notification.read_at ? 'font-semibold text-gray-900' : 'text-gray-700'} leading-relaxed`}>
                                                {notification.data.message}
                                            </p>
                                            {notification.data.batch_role && (
                                                <div className="mt-2 flex flex-wrap gap-1 sm:gap-2">
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                        {notification.data.batch_role.toUpperCase()}
                                                    </span>
                                                    {notification.data.batch_amount != null && (
                                                        <span className="text-xs text-gray-600">
                                                            {Number(notification.data.batch_amount).toLocaleString('fr-FR', {
                                                                style: 'currency',
                                                                currency: 'MAD'
                                                            })}
                                                            {Number(notification.data.batch_credit_applied || 0) > 0 && (
                                                                <span className="text-gray-400 ml-1">
                                                                    ({Number(notification.data.batch_total_net).toLocaleString('fr-FR')} total − {Number(notification.data.batch_credit_applied).toLocaleString('fr-FR')} déjà en caisse)
                                                                </span>
                                                            )}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                            <p className="text-xs text-gray-500 mt-2 flex items-center">
                                                {!notification.read_at && (
                                                    <span className="inline-block w-2 h-2 bg-red-600 rounded-full mr-2"></span>
                                                )}
                                                {formatTimeAgo(notification.created_at)}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    
                    {/* Footer */}
                    {notifications.length > 0 && (
                        <div className="p-3 border-t border-gray-200 text-center">
                            <button
                                onClick={handleViewAllNotifications}
                                className="text-xs sm:text-sm text-red-600 hover:text-red-700 font-medium transition-colors"
                            >
                                Voir toutes les notifications
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationBell;

